import * as Sentry from '@sentry/react';
import { lazy, Suspense, useContext, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { LAST_USED_ACCOUNT_STORAGE_KEY } from './constants/storage';
import { AccountActivationChecklistProvider } from './contexts/AccountActivationChecklistContext';
import { useAnalytics } from './contexts/AnalyticsContext';
import { AuthContext } from './contexts/AuthContext';
import { DashboardContextProvider } from './contexts/DashboardContext';
import { DynamicPageContextProvider } from './contexts/DynamicPageContext';
import { LimitExceededProvider } from './contexts/LimitExceededContext';
import './App.css';
import RequireAllowedAccess from './components/Common/RequireAllowedAccess';
import RequireOnboardingComplete from './components/Common/RequireOnboardingComplete';
import { AISuggestionsContextProvider } from './contexts/AISuggestionsContextProvider';
import { AgentRunNavigationProvider } from './contexts/AgentRunNavigationContext';
import { ExportAnalyticsContextProvider } from './contexts/ExportAnalyticsContext';
import { ExportLeadsContextProvider } from './contexts/ExportLeadsContext';
import { EDITOR_ROUTES } from './services/dashboardRouteTo';

// Names Sentry transactions after the matched route pattern
// (`/workspace/:slug/pages/:id`) instead of the raw URL, so timings aggregate
// per screen rather than per entity id, and emits a span per client-side
// navigation.
//
// Resolved on first render, not at module scope: `wrapReactRouterRouting`
// silently returns `Routes` unwrapped unless `Sentry.init()` has already run,
// and this module is evaluated by index.tsx's `import App` before it reaches
// `Sentry.init()`. Cached so the component identity stays stable across
// renders — a new identity would remount the whole route tree.
let routesComponent: typeof Routes | undefined;

const getRoutesComponent = (): typeof Routes =>
  (routesComponent ??= Sentry.wrapReactRouterRouting(Routes));

// Lazy-loaded route components
const DashboardLayout = lazy(
  () => import('./components/Common/DashboardLayout'),
);
const Login = lazy(() => import('./screens/Login'));
const Workspaces = lazy(() => import('./screens/Workspaces'));
const Onboard = lazy(() => import('./screens/Onboard'));
const ReportsPage = lazy(() => import('./screens/ReportsPage'));
const DynamicPages = lazy(() => import('./screens/DynamicPages'));
const QuizFunnels = lazy(() => import('./screens/QuizFunnels'));
const PageEditor = lazy(() => import('./screens/PageEditor'));
const QuizEditor = lazy(() => import('./screens/QuizEditor'));
const GlobalSectionEditor = lazy(() => import('./screens/GlobalSectionEditor'));
const ManageDynamicPage = lazy(
  () => import('./screens/ManageDynamicPage/ManageDynamicPage'),
);
const ManageDynamicPageDetails = lazy(
  () => import('./screens/ManageDynamicPage/ManageDynamicPageDetails'),
);
const ManageDynamicPageAnalytics = lazy(
  () => import('./screens/ManageDynamicPage/ManageDynamicPageAnalytics'),
);
const ManageDynamicPageLeads = lazy(
  () => import('./screens/ManageDynamicPage/ManageDynamicPageLeads'),
);
const ManageDynamicPageExperiments = lazy(
  () => import('./screens/ManageDynamicPage/ManageDynamicPageExperiments'),
);
const AdCampaigns = lazy(() => import('./screens/AdCampaigns/AdCampaignsPage'));
const Leads = lazy(() => import('./screens/Leads'));
const ExperimentsHub = lazy(() =>
  import('./screens/ExperimentsHub/ExperimentsHub').then((m) => ({
    default: m.ExperimentsHub,
  })),
);
const Superagent = lazy(() =>
  import('@/screens/Superagent.tsx').then((m) => ({ default: m.Superagent })),
);
const Brain = lazy(() => import('./screens/Brain'));
const BrainKnowledge = lazy(() => import('./screens/brain/BrainKnowledge'));
const BrainSkills = lazy(() => import('./screens/brain/BrainSkills'));
const BrainConnectors = lazy(() => import('./screens/brain/BrainConnectors'));
const BrainMemory = lazy(() => import('./screens/brain/BrainMemory'));
const Forms = lazy(() => import('./screens/Forms'));
const SettingsWrapper = lazy(
  () => import('./screens/settings/SettingsWrapper'),
);
const WebsiteSettings = lazy(
  () => import('@/screens/settings/WebsiteSettings.tsx'),
);
const AiSettings = lazy(() => import('./screens/settings/AiSettings'));
const AdAccountsSettings = lazy(
  () => import('./screens/settings/AdAccountsSettings'),
);
const EmbeddedFormsSettings = lazy(
  () => import('./screens/settings/EmbeddedFormsSettings'),
);
const IntegrationsSettings = lazy(
  () => import('./screens/settings/IntegrationsSettings'),
);
const AppsTab = lazy(() => import('./screens/settings/integrations/AppsTab'));
const ShopifyInstallClaim = lazy(() => import('./screens/ShopifyInstallClaim'));
const WebhooksTab = lazy(
  () => import('./screens/settings/integrations/WebhooksTab'),
);
const McpTab = lazy(() => import('./screens/settings/integrations/McpTab'));
const OAuthConsent = lazy(() => import('./screens/OAuthConsent'));
const ApiKeysTab = lazy(
  () => import('./screens/settings/integrations/ApiKeysTab'),
);
const BillingSettings = lazy(
  () => import('./screens/settings/BillingSettings'),
);
const ProfileAndMembers = lazy(
  () => import('@/screens/settings/ProfileAndMembers.tsx'),
);
const Agents = lazy(() => import('./screens/Agents/AgentsPage'));
const AgentDetail = lazy(() => import('./screens/Agents/AgentDetailPage'));
const AgentForm = lazy(() => import('./screens/Agents/AgentFormPage'));
const Opportunities = lazy(
  () => import('./screens/Opportunities/OpportunitiesPage'),
);
const Alerts = lazy(() => import('./screens/Alerts/AlertsPage'));
const ContentLibrarySettings = lazy(
  () => import('@/screens/settings/ContentLibrarySettings.tsx'),
);

const ProtectedRoute = ({
  isAuthenticated,
  redirect,
  addGoTo,
}: {
  isAuthenticated: boolean | null;
  redirect?: string | (() => string);
  addGoTo?: boolean;
}) => {
  if (!isAuthenticated) {
    const params = [
      ...(window.location.pathname !== '/' && addGoTo
        ? ['?goto=', window.location.pathname]
        : []),
      window.location.search,
    ]
      .filter((a) => !!a)
      .filter((_, __, arr) => arr.length > 1)
      .join('');
    const redirectTo =
      !redirect || typeof redirect === 'string'
        ? redirect || '/login'
        : redirect();
    return <Navigate to={redirectTo + params} replace />;
  }

  return <Outlet />;
};

function App() {
  const SentryRoutes = getRoutesComponent();
  const location = useLocation();

  const { isAuthenticated } = useContext(AuthContext);
  const { track, trackPageView } = useAnalytics();

  useEffect(() => {
    track('web_app_startup');
  }, [track]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location, trackPageView]);

  return (
    <div className="h-dvh overflow-y-auto lg:h-screen">
      <Suspense>
        <SentryRoutes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <ProtectedRoute addGoTo isAuthenticated={isAuthenticated} />
            }
          >
            <Route element={<RequireAllowedAccess />}>
              <Route
                path=""
                element={
                  localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY) ? (
                    <Navigate
                      to={`/workspace/${localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY)}`}
                    />
                  ) : (
                    <Navigate to="/workspaces" />
                  )
                }
              />
              <Route path="/workspaces" element={<Workspaces />} />
              <Route path="/oauth/consent" element={<OAuthConsent />} />
              {/* Shopify sends an install here once the merchant has granted
                  access. The claim is a path segment rather than a query so it
                  survives the login redirect, which forwards the pathname as
                  `goto` and drops a lone query string. */}
              <Route
                path="/shopify/claim/:claimId"
                element={<ShopifyInstallClaim />}
              />
              <Route path="/onboard" element={<Onboard />} />
              <Route path="/onboard/:accountId" element={<Onboard />} />
              <Route
                path="/workspace/:slug"
                element={<RequireOnboardingComplete />}
              >
                <Route
                  element={
                    <DashboardContextProvider>
                      <LimitExceededProvider>
                        <AISuggestionsContextProvider>
                          <AccountActivationChecklistProvider>
                            <ExportAnalyticsContextProvider>
                              <ExportLeadsContextProvider>
                                <AgentRunNavigationProvider>
                                  <DashboardLayout />
                                </AgentRunNavigationProvider>
                              </ExportLeadsContextProvider>
                            </ExportAnalyticsContextProvider>
                          </AccountActivationChecklistProvider>
                        </AISuggestionsContextProvider>
                      </LimitExceededProvider>
                    </DashboardContextProvider>
                  }
                >
                  {/* Reports is the default route */}
                  <Route path="reports/:reportId?" element={<ReportsPage />} />
                  <Route
                    path="campaigns"
                    element={<Navigate to="../pages" />}
                  />
                  <Route path="pages" element={<DynamicPages />} />
                  <Route
                    path={EDITOR_ROUTES.globalSection}
                    element={<GlobalSectionEditor />}
                  />
                  <Route path="quiz-funnels" element={<QuizFunnels />} />
                  <Route path={EDITOR_ROUTES.quiz} element={<QuizEditor />} />
                  <Route
                    path={EDITOR_ROUTES.embeddedQuiz}
                    element={<QuizEditor embedded />}
                  />
                  <Route path={EDITOR_ROUTES.page} element={<PageEditor />} />
                  <Route
                    path="pages/:dynamicPageId"
                    element={
                      <DynamicPageContextProvider>
                        <ManageDynamicPage />
                      </DynamicPageContextProvider>
                    }
                  >
                    <Route
                      path="details"
                      element={<ManageDynamicPageDetails />}
                    />
                    <Route
                      path="analytics"
                      element={<ManageDynamicPageAnalytics />}
                    />
                    <Route path="leads" element={<ManageDynamicPageLeads />} />
                    <Route
                      path="experiments"
                      element={<ManageDynamicPageExperiments />}
                    />

                    <Route
                      path="*"
                      element={
                        <Navigate
                          replace
                          to={{ pathname: 'details', search: location.search }}
                        />
                      }
                    />
                    <Route
                      path=""
                      element={
                        <Navigate
                          replace
                          to={{ pathname: 'details', search: location.search }}
                        />
                      }
                    />
                  </Route>
                  <Route path="ad-campaigns" element={<AdCampaigns />} />
                  <Route
                    path="ad-campaigns/ad-sets"
                    element={<AdCampaigns tab="adSets" />}
                  />
                  <Route
                    path="ad-campaigns/ads"
                    element={<AdCampaigns tab="ads" />}
                  />
                  <Route
                    path="search-campaigns"
                    element={<Navigate replace to="../ad-campaigns" />}
                  />
                  {/* <Route path="aaa" element={<Agent />} /> */}
                  <Route
                    path="social-campaigns"
                    element={<Navigate replace to="../ad-campaigns" />}
                  />
                  <Route path="leads" element={<Leads />} />
                  <Route
                    path="leads/partial"
                    element={<Leads viewType="partialLeads" />}
                  />
                  <Route path="experiments" element={<ExperimentsHub />} />
                  <Route path="agents" element={<Agents />} />
                  <Route path="agents/new" element={<AgentForm />} />
                  <Route path="agents/:agentId" element={<AgentDetail />} />
                  <Route path="agents/:agentId/edit" element={<AgentForm />} />
                  <Route path="opportunities" element={<Opportunities />} />
                  <Route path="alerts" element={<Alerts />} />
                  <Route
                    path="superagent/:threadId?"
                    element={<Superagent />}
                  />
                  <Route
                    path="assistant"
                    element={<Navigate replace to="../superagent" />}
                  />
                  <Route path="brain" element={<Brain />}>
                    <Route path="knowledge" element={<BrainKnowledge />} />
                    <Route path="skills" element={<BrainSkills />} />
                    <Route path="skills/:skillId" element={<BrainSkills />} />
                    <Route path="connectors" element={<BrainConnectors />} />
                    <Route path="memory" element={<BrainMemory />} />
                    <Route
                      path=""
                      element={<Navigate replace to="connectors" />}
                    />
                    <Route
                      path="*"
                      element={<Navigate replace to="connectors" />}
                    />
                  </Route>
                  <Route path="settings" element={<SettingsWrapper />}>
                    <Route path="website" element={<WebsiteSettings />}>
                      <Route path=":tab" />
                    </Route>
                    <Route path="ai-settings" element={<AiSettings />}>
                      <Route path=":tab" />
                    </Route>
                    <Route path="forms" element={<Forms />} />
                    <Route
                      path="ad-accounts"
                      element={
                        <Navigate replace to="../integrations/ad-accounts" />
                      }
                    />
                    <Route
                      path="embedded-forms"
                      element={<EmbeddedFormsSettings />}
                    />
                    <Route
                      path="integrations"
                      element={<IntegrationsSettings />}
                    >
                      <Route path="apps" element={<AppsTab />} />
                      <Route path="webhooks" element={<WebhooksTab />} />
                      <Route path="api-keys" element={<ApiKeysTab />} />
                      <Route path="mcp" element={<McpTab />} />
                      <Route
                        path="ad-accounts"
                        element={<AdAccountsSettings />}
                      />
                      <Route path="" element={<Navigate replace to="apps" />} />
                      <Route
                        path="*"
                        element={<Navigate replace to="apps" />}
                      />
                    </Route>
                    <Route
                      path="connectors"
                      element={<Navigate replace to="../../brain/connectors" />}
                    />
                    <Route path="billing" element={<BillingSettings />}>
                      <Route path=":tab" />
                    </Route>
                    <Route path="organization" element={<ProfileAndMembers />}>
                      <Route path=":tab" />
                    </Route>
                    <Route path="cms" element={<ContentLibrarySettings />} />
                    <Route
                      path=""
                      element={<Navigate replace to="website" />}
                    />
                  </Route>
                  <Route
                    path="*"
                    element={<Navigate replace to="superagent" />}
                  />
                  <Route
                    path=""
                    element={<Navigate replace to="superagent" />}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate replace to="" />} />
        </SentryRoutes>
      </Suspense>
    </div>
  );
}

export default App;
